// Copyright ©️ 2026 eVolve MEP, LLC

import { Stack, Text } from '@mantine/core';

import { NumberInputWrapped, type NumberInputWrappedProps } from 'components/Mantine/NumberInputWrapped';
import { ToggleRadioGroup } from 'components/Mantine/TypeSafeSelect/ToggleRadioGroup';
import { isNotNil } from 'helpers/isNotNil';

import classes from './BendDimensionInput.module.css';

/** Standard angles offered as quick-fill buttons, in degrees. */
const QUICK_FILL_ANGLES = [5, 10, 11.25, 22.5, 30, 45, 60, 90] as const;

const quickFillOptions = QUICK_FILL_ANGLES.map((angle) => ({
  id: `${angle}`,
  value: angle,
  label: (
    <Text span ff="monospace" fz="sm" fw="inherit">
      {angle}°
    </Text>
  ),
}));

type Props = NumberInputWrappedProps & {
  /** Called with the picked angle, in degrees, when a quick-fill button is tapped. */
  onQuickFill: (angle: number) => void;
};

/**
 * A touch-sized angle field, in degrees, with a grid of quick-fill buttons for standard angles.
 * The quick-fill button matching the current angle is highlighted.
 */
export const BendAngleInput = ({ onQuickFill, ...numberInputProps }: Props) => (
  <Stack gap="xs">
    <NumberInputWrapped
      size="xl"
      suffix="°"
      decimalScale={2}
      hideControls
      classNames={{ label: classes.label, input: classes.input }}
      {...numberInputProps}
    />
    <ToggleRadioGroup
      data={quickFillOptions}
      value={numberInputProps.value}
      onChange={(angle) => {
        if (isNotNil(angle)) onQuickFill(angle);
      }}
      disabled={numberInputProps.disabled || numberInputProps.readOnly}
      size="sm"
      variant="card"
      cols={4}
    />
  </Stack>
);
